/**
 * FreeFormTemplatePage.jsx — Bölüm 25-C
 * Free Form şablon yönetim ekranı.
 *
 * Özellikler:
 *  - Şablon listesi (targetEntityType filtresi, sayfalama)
 *  - Şablon oluştur / düzenle / sil (409 lock koruması)
 *  - Node editörü: kategori ve soru ekle/düzenle/sil
 *  - ⚠️ Edit Lock: herhangi bir submission varsa şablon + node'lar immutable (409)
 */
import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Card, CardBody, Row, Col, Badge, Button,
  Input, Modal, ModalHeader, ModalBody, ModalFooter,
  Alert, Spinner, Label, FormGroup,
} from "reactstrap";
import {
  Plus, Trash2, Edit2, ChevronLeft, ChevronRight,
  FolderOpen, HelpCircle, Lock,
  AlertTriangle, X as XIcon, Save, List,
} from "lucide-react";
import { PageFrame, PageBody } from "../../../Components/Common/EmbeddablePage";
import {
  createTemplate, listTemplates, getTemplateTree,
  updateTemplate, deleteTemplate,
  addNode, updateNode, deleteNode,
  flatFreeFormNodes,
} from "../../../services/freeFormService";
import { useTranslation } from "react-i18next";
import i18n from "../../../i18n";

// ─── Sabitler ───────────────────────────────────────────────────────────────

const ENTITY_TYPES = [
  { value: "",         get label() { return i18n.t("common:tumu"); } },
  { value: "FAIR",     get label() { return i18n.t("formlar:freeFormTemplate.fuar"); } },
  { value: "SALON",    get label() { return i18n.t("formlar:freeFormTemplate.salon"); } },
  { value: "STAND",    get label() { return i18n.t("formlar:freeFormTemplate.stand"); } },
  { value: "PERSON",   get label() { return i18n.t("formlar:freeFormTemplate.kisi"); } },
];

const ENTITY_COLORS = {
  FAIR:   "primary",
  SALON:  "info",
  STAND:  "success",
  PERSON: "warning",
};

const ANSWER_TYPES = [
  { value: "TEXT",          get label() { return i18n.t("formlar:freeFormTemplate.metin"); } },
  { value: "NUMBER",        get label() { return i18n.t("formlar:freeFormTemplate.sayi"); } },
  { value: "SINGLE_CHOICE", get label() { return i18n.t("formlar:freeFormTemplate.tekSecim"); } },
  { value: "MULTI_CHOICE",  get label() { return i18n.t("formlar:freeFormTemplate.cokluSecim"); } },
  { value: "PHOTO",         get label() { return i18n.t("formlar:freeFormTemplate.fotograf"); } },
];

const ANSWER_TYPE_ICONS = {
  TEXT:          "bx bx-text",
  NUMBER:        "bx bx-hash",
  SINGLE_CHOICE: "bx bx-radio-circle-marked",
  MULTI_CHOICE:  "bx bx-checkbox-checked",
  PHOTO:         "bx bx-camera",
};

const DEFAULT_TEMPLATE_FORM = {
  name: "",
  description: "",
  targetEntityType: "STAND",
  layers: [
    { order: 0, name: "Bölüm",           type: "CATEGORY" },
    { order: 1, name: "Kontrol Maddesi", type: "QUESTION" },
  ],
};

const DEFAULT_NODE_FORM = {
  type: "CATEGORY",        // "CATEGORY" | "QUESTION"
  name: "",
  parentId: "",
  answerType: "SINGLE_CHOICE",
  choices: ["Evet", "Hayır"],
  required: false,
};

// ─── Yardımcı Bileşenler ─────────────────────────────────────────────────────

function LockBadge() {
  const { t } = useTranslation("formlar");
  return (
    <Badge color="warning" pill className="d-flex align-items-center gap-1" style={{ fontSize: "0.65rem" }}>
      <Lock size={10} />{" "}{t("freeFormTemplate.kilitli")}
    </Badge>
  );
}

function EntityBadge({ type }) {
  const label = ENTITY_TYPES.find((e) => e.value === type)?.label || type;
  return <Badge color={ENTITY_COLORS[type] || "secondary"} pill>{label}</Badge>;
}

function AnswerTypeIcon({ type }) {
  return <i className={`${ANSWER_TYPE_ICONS[type] || "bx bx-question-mark"} me-1`} />;
}

// ─── Modal: Şablon Oluştur / Düzenle ─────────────────────────────────────────

function TemplateFormModal({ open, template, onClose, onSaved }) {
  const { t } = useTranslation("formlar");
  const isEdit = !!template;
  const [form,    setForm]    = useState(DEFAULT_TEMPLATE_FORM);
  const [saving,  setSaving]  = useState(false);
  const [error,   setError]   = useState(null);

  // Modal açıldığında formu doldur
  useEffect(() => {
    function init() {
      if (!open) return;
      if (isEdit) {
        setForm({
          name:             template.name || "",
          description:      template.description || "",
          targetEntityType: template.targetEntityType || "STAND",
          layers:           template.layers || DEFAULT_TEMPLATE_FORM.layers,
        });
      } else {
        setForm(DEFAULT_TEMPLATE_FORM);
      }
      setError(null);
    }
    init();
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  async function handleSubmit() {
    if (!form.name.trim()) { setError(t("freeFormTemplate.sablonAdiZorunlu")); return; }
    setSaving(true);
    setError(null);
    try {
      if (isEdit) {
        await updateTemplate(template._id || template.id, {
          name:             form.name,
          description:      form.description,
          targetEntityType: form.targetEntityType,
        });
      } else {
        await createTemplate({
          name:             form.name,
          description:      form.description,
          targetEntityType: form.targetEntityType,
          layers:           form.layers,
        });
      }
      onSaved();
      onClose();
    } catch (err) {
      const msg = err?.response?.data?.message || err.message;
      if (err?.response?.status === 409) {
        setError(t("freeFormTemplate.buSablonKilitliMevcutSubmission"));
      } else {
        setError(msg || t("freeFormTemplate.birHataOlustu"));
      }
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal isOpen={open} toggle={onClose} size="lg" centered>
      <ModalHeader toggle={onClose}>
        {isEdit ? t("freeFormTemplate.sablonuDuzenle") : t("freeFormTemplate.yeniSablonOlustur")}
      </ModalHeader>
      <ModalBody>
        {error && <Alert color="danger" className="mb-3">{error}</Alert>}

        <FormGroup className="mb-3">
          <Label className="fw-semibold small">{t("freeFormTemplate.sablonAdi")}{" "}<span className="text-danger">*</span></Label>
          <Input
            value={form.name}
            onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
            placeholder={t("freeFormTemplate.ornStandKurulumKontrolFormu")}
          />
        </FormGroup>

        <FormGroup className="mb-3">
          <Label className="fw-semibold small">{t("freeFormTemplate.aciklama")}</Label>
          <Input
            type="textarea"
            rows={2}
            value={form.description}
            onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
            placeholder={t("freeFormTemplate.istegeBagliKisaAciklama")}
          />
        </FormGroup>

        <FormGroup className="mb-3">
          <Label className="fw-semibold small">{t("freeFormTemplate.hedefVarlikTuru")}</Label>
          <Input
            type="select"
            value={form.targetEntityType}
            disabled={isEdit}
            onChange={(e) => setForm((p) => ({ ...p, targetEntityType: e.target.value }))}
          >
            {ENTITY_TYPES.filter((e) => e.value).map((e) => (
              <option key={e.value} value={e.value}>{e.label}</option>
            ))}
          </Input>
          {isEdit && (
            <div className="text-muted small mt-1">
              <Lock size={11} className="me-1" />{t("freeFormTemplate.hedefVarlikTuruSablonOlusturulduktan")}
            </div>
          )}
        </FormGroup>

        {/* Katman tanımları — sadece yeni şablonda göster */}
        {!isEdit && (
          <div className="border rounded p-3 bg-light">
            <div className="fw-semibold small mb-2 text-muted text-uppercase">{t("freeFormTemplate.katmanTanimlari")}</div>
            {form.layers.map((layer, idx) => (
              <div key={idx} className="d-flex align-items-center gap-2 mb-2">
                <Input
                  type="select"
                  bsSize="sm"
                  style={{ width: 110, flexShrink: 0 }}
                  value={layer.type}
                  onChange={(e) => {
                    const updated = [...form.layers];
                    updated[idx] = { ...updated[idx], type: e.target.value };
                    setForm((p) => ({ ...p, layers: updated }));
                  }}
                >
                  <option value="CATEGORY">{t("freeFormTemplate.bolum")}</option>
                  <option value="QUESTION">{t("freeFormTemplate.soru")}</option>
                </Input>
                <Input
                  value={layer.name}
                  size="sm"
                  className="flex-grow-1"
                  onChange={(e) => {
                    const updated = [...form.layers];
                    updated[idx] = { ...updated[idx], name: e.target.value };
                    setForm((p) => ({ ...p, layers: updated }));
                  }}
                  placeholder={t("freeFormTemplate.katmanAdi")}
                />
                <button
                  type="button"
                  className="btn btn-sm btn-outline-danger px-2"
                  disabled={form.layers.length <= 2}
                  title={form.layers.length <= 2 ? t("freeFormTemplate.enAz2KatmanZorunlu") : t("freeFormTemplate.katmaniSil")}
                  onClick={() => {
                    const updated = form.layers
                      .filter((_, i) => i !== idx)
                      .map((l, i) => ({ ...l, order: i }));
                    setForm((p) => ({ ...p, layers: updated }));
                  }}
                >
                  <XIcon size={13} />
                </button>
              </div>
            ))}
            <button
              type="button"
              className="btn btn-sm btn-outline-primary mt-1"
              onClick={() => {
                const newLayer = { order: form.layers.length, name: "", type: "QUESTION" };
                setForm((p) => ({ ...p, layers: [...p.layers, newLayer] }));
              }}
            >
              <Plus size={13} className="me-1" />
              {t("freeFormTemplate.katmanEkle")}
            </button>
            <div className="text-muted small mt-2">
              <i className="bx bx-info-circle me-1" />
              {t("freeFormTemplate.istediginizKadarKatmanBolumSoru")}
            </div>
          </div>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="light" onClick={onClose} disabled={saving}>{t("common:iptal")}</Button>
        <Button color="primary" onClick={handleSubmit} disabled={saving}>
          {saving ? <Spinner size="sm" className="me-1" /> : <Save size={14} className="me-1" />}
          {isEdit ? t("freeFormTemplate.guncelle") : t("freeFormTemplate.olustur")}
        </Button>
      </ModalFooter>
    </Modal>
  );
}

// ─── Modal: Şablon Sil Onay ──────────────────────────────────────────────────

function DeleteTemplateModal({ open, template, onClose, onDeleted }) {
  const { t } = useTranslation("formlar");
  const [deleting, setDeleting] = useState(false);
  const [error,    setError]    = useState(null);

  async function handleDelete() {
    setDeleting(true);
    setError(null);
    try {
      await deleteTemplate(template._id || template.id);
      onDeleted();
      onClose();
    } catch (err) {
      if (err?.response?.status === 409) {
        setError(t("freeFormTemplate.buSablonSilinemezMevcutSubmission"));
      } else {
        setError(err?.response?.data?.message || err.message || t("freeFormTemplate.silmeHatasi"));
      }
    } finally {
      setDeleting(false);
    }
  }

  if (!template) return null;

  return (
    <Modal isOpen={open} toggle={onClose} centered size="sm">
      <ModalHeader toggle={onClose} className="text-danger">{t("freeFormTemplate.sablonuSil")}</ModalHeader>
      <ModalBody>
        {error && <Alert color="danger" className="mb-3">{error}</Alert>}
        <p className="mb-1">
          <strong>"{template.name}"</strong>{" "}{t("freeFormTemplate.sablonunuSilmekIstediginizdenEminMisiniz")}
        </p>
        {(template.submissionCount > 0) && (
          <Alert color="warning" className="mt-2 small d-flex align-items-center gap-2">
            <AlertTriangle size={14} />
            {t("freeFormTemplate.buSablonaAit")}{" "}<b>{template.submissionCount}</b>{" "}{t("freeFormTemplate.doldurulmusFormKaydiVarSilme")}
          </Alert>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="light" onClick={onClose} disabled={deleting}>{t("common:iptal")}</Button>
        <Button color="danger" onClick={handleDelete} disabled={deleting}>
          {deleting ? <Spinner size="sm" className="me-1" /> : <Trash2 size={14} className="me-1" />}
          {t("common:sil")}
        </Button>
      </ModalFooter>
    </Modal>
  );
}

// ─── Node Editörü ─────────────────────────────────────────────────────────────

function NodeModal({ open, mode, node, categoryLayer, questionLayer, templateId, categories, targetLayerOrder, onClose, onSaved }) {
  const { t: tx } = useTranslation("formlar");
  // mode: "add-category" | "add-question" | "edit"
  const [form,   setForm]   = useState(DEFAULT_NODE_FORM);
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState(null);

  useEffect(() => {
    function init() {
      if (!open) return;
      setError(null);
      if (mode === "add-category") {
        setForm({ ...DEFAULT_NODE_FORM, type: "CATEGORY", name: "", parentId: node?.parentId || "" });
      } else if (mode === "add-question") {
        // node.parentId: soru bu butondan açıldıysa tıklanan kategorinin ID'si
        const defaultParentId = node?.parentId || categories[0]?._id || categories[0]?.id || "";
        console.log('[NodeModal] add-question init | node.parentId:', node?.parentId, '| categories[0]:', categories[0]?._id || categories[0]?.id, '| kullanılan parentId:', defaultParentId);
        setForm({ ...DEFAULT_NODE_FORM, type: "QUESTION", name: "", parentId: defaultParentId });
      } else if (mode === "edit" && node) {
        const isQ = node.layerOrder === 1 || node.type === "QUESTION";
        setForm({
          type:       isQ ? "QUESTION" : "CATEGORY",
          name:       node.name || "",
          parentId:   node.parentId || "",
          answerType: node.answerType || "SINGLE_CHOICE",
          choices:    node.choices || ["Evet", "Hayır"],
          required:   node.required || false,
        });
      }
    }
    init();
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const isQuestion = form.type === "QUESTION";
  const hasChoices = form.answerType === "SINGLE_CHOICE" || form.answerType === "MULTI_CHOICE";

  async function handleSubmit() {
    if (!form.name.trim()) { setError(tx("freeFormTemplate.adZorunlu")); return; }
    if (isQuestion && !form.parentId && categories.length > 0) { setError(tx("freeFormTemplate.ustBolumSecin")); return; }
    setSaving(true);
    setError(null);
    try {
      const body = {
        layerOrder: targetLayerOrder !== undefined
          ? Number(targetLayerOrder)
          : (isQuestion ? questionLayer?.order ?? 1 : categoryLayer?.order ?? 0),
        name:       form.name.trim(),
        // Sorular için üst kategori zorunlu; alt kategoriler için de parentId gönder
        ...((isQuestion || (!isQuestion && form.parentId)) && { parentId: String(form.parentId) }),
        ...(isQuestion && { answerType: form.answerType }),
        ...(isQuestion && hasChoices && { choices: form.choices.filter((c) => c.trim()) }),
        ...(isQuestion && { required: form.required }),
      };
      console.log('[NodeModal] handleSubmit | mode:', mode, '| isQuestion:', isQuestion, '| body:', JSON.stringify(body));
      if (mode === "edit" && node) {
        await updateNode(node._id || node.id, body);
      } else {
        await addNode(templateId, body);
      }
      console.log('[NodeModal] kayıt başarılı → loadTree tetikleniyor');
      onSaved();
      onClose();
    } catch (err) {
      if (err?.response?.status === 409) {
        setError(tx("freeFormTemplate.sablonKilitliSubmissionKayitlariVar"));
      } else {
        setError(err?.response?.data?.message || err.message || tx("freeFormTemplate.hataOlustu"));
      }
    } finally {
      setSaving(false);
    }
  }

  const title = mode === "edit"
    ? tx("freeFormTemplate.duzenle", { v: node?.name || "" })
    : isQuestion
      ? tx("freeFormTemplate.yeniEkle", { v: questionLayer?.name || tx("freeFormTemplate.soru") })
      : tx("freeFormTemplate.yeniEkle", { v: categoryLayer?.name || tx("freeFormTemplate.bolum") });

  return (
    <Modal isOpen={open} toggle={onClose} centered>
      <ModalHeader toggle={onClose}>{title}</ModalHeader>
      <ModalBody>
        {error && <Alert color="danger">{error}</Alert>}

        {/* Soru ise → üst kategori seç */}
        {isQuestion && categories.length > 0 && (
          <FormGroup className="mb-3">
            <Label className="fw-semibold small">
              {tx("freeFormTemplate.ust")}{" "}{categoryLayer?.name || tx("freeFormTemplate.bolum")} <span className="text-danger">*</span>
            </Label>
            <Input
              type="select"
              value={form.parentId}
              onChange={(e) => setForm((p) => ({ ...p, parentId: e.target.value }))}
            >
              {categories.map((cat) => (
                <option key={cat._id || cat.id} value={cat._id || cat.id}>
                  {cat.name}
                </option>
              ))}
            </Input>
          </FormGroup>
        )}

        <FormGroup className="mb-3">
          <Label className="fw-semibold small">
            {isQuestion ? tx("freeFormTemplate.metni", { v: questionLayer?.name || tx("freeFormTemplate.soru") }) : tx("freeFormTemplate.adi", { v: categoryLayer?.name || tx("freeFormTemplate.bolum") })}
            <span className="text-danger ms-1">*</span>
          </Label>
          <Input
            type="textarea"
            rows={2}
            value={form.name}
            onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
            placeholder={isQuestion ? tx("freeFormTemplate.soruMetniniGirin") : tx("freeFormTemplate.bolumAdiniGirin")}
          />
        </FormGroup>

        {isQuestion && (
          <>
            <FormGroup className="mb-3">
              <Label className="fw-semibold small">{tx("freeFormTemplate.cevapTipi")}</Label>
              <Input
                type="select"
                value={form.answerType}
                onChange={(e) => setForm((p) => ({ ...p, answerType: e.target.value }))}
              >
                {ANSWER_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </Input>
            </FormGroup>

            {hasChoices && (
              <FormGroup className="mb-3">
                <Label className="fw-semibold small">{tx("freeFormTemplate.secenekler")}</Label>
                {form.choices.map((choice, ci) => (
                  <div key={ci} className="d-flex gap-2 mb-2">
                    <Input
                      value={choice}
                      size="sm"
                      onChange={(e) => {
                        const updated = [...form.choices];
                        updated[ci] = e.target.value;
                        setForm((p) => ({ ...p, choices: updated }));
                      }}
                      placeholder={tx("freeFormTemplate.secenek", { v: ci + 1 })}
                    />
                    <Button
                      color="light"
                      size="sm"
                      disabled={form.choices.length <= 1}
                      onClick={() => setForm((p) => ({ ...p, choices: p.choices.filter((_, i) => i !== ci) }))}
                    >
                      <XIcon size={13} />
                    </Button>
                  </div>
                ))}
                <Button
                  color="soft-primary"
                  size="sm"
                  className="mt-1"
                  onClick={() => setForm((p) => ({ ...p, choices: [...p.choices, ""] }))}
                >
                  <Plus size={13} className="me-1" />{" "}{tx("freeFormTemplate.secenekEkle")}
                </Button>
              </FormGroup>
            )}

            <FormGroup check className="mb-3">
              <Input
                type="checkbox"
                id="node-required"
                checked={form.required}
                onChange={(e) => setForm((p) => ({ ...p, required: e.target.checked }))}
              />
              <Label check for="node-required" className="small ms-2">{tx("freeFormTemplate.zorunluAlan")}</Label>
            </FormGroup>
          </>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="light" onClick={onClose} disabled={saving}>{tx("common:iptal")}</Button>
        <Button color="primary" onClick={handleSubmit} disabled={saving}>
          {saving ? <Spinner size="sm" className="me-1" /> : <Save size={14} className="me-1" />}
          {tx("common:kaydet")}
        </Button>
      </ModalFooter>
    </Modal>
  );
}

// ─── Modal: Node Sil Onay ─────────────────────────────────────────────────────

function DeleteNodeModal({ open, node, onClose, onDeleted }) {
  const { t } = useTranslation("formlar");
  const [deleting, setDeleting] = useState(false);
  const [error,    setError]    = useState(null);

  async function handleDelete() {
    setDeleting(true);
    setError(null);
    try {
      await deleteNode(node._id || node.id);
      onDeleted();
      onClose();
    } catch (err) {
      if (err?.response?.status === 409) {
        setError(t("freeFormTemplate.sablonKilitliSubmissionKayitlariVar2"));
      } else {
        setError(err?.response?.data?.message || err.message || t("freeFormTemplate.silmeHatasi"));
      }
    } finally {
      setDeleting(false);
    }
  }

  if (!node) return null;

  const isCategory = node.layerOrder === 0 || node.type === "CATEGORY";

  return (
    <Modal isOpen={open} toggle={onClose} centered size="sm">
      <ModalHeader toggle={onClose} className="text-danger">
        {isCategory ? t("freeFormTemplate.bolumuSil") : t("freeFormTemplate.soruyuSil")}
      </ModalHeader>
      <ModalBody>
        {error && <Alert color="danger">{error}</Alert>}
        <p className="mb-1">
          <strong>"{node.name}"</strong> {isCategory ? t("freeFormTemplate.bolumunu") : t("freeFormTemplate.sorusunu")}{" "}{t("freeFormTemplate.silmekIstiyorMusunuz")}
        </p>
        {isCategory && (
          <Alert color="warning" className="small mt-2 d-flex align-items-center gap-2">
            <AlertTriangle size={13} />
            {t("freeFormTemplate.buBolumeAitTumSorular")}
          </Alert>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="light" onClick={onClose} disabled={deleting}>{t("common:iptal")}</Button>
        <Button color="danger" onClick={handleDelete} disabled={deleting}>
          {deleting ? <Spinner size="sm" className="me-1" /> : <Trash2 size={14} className="me-1" />}
          {t("common:sil")}
        </Button>
      </ModalFooter>
    </Modal>
  );
}

// ─── Node Editörü Paneli ─────────────────────────────────────────────────────

function NodeEditor({ template, onBack, onViewSubmissions }) {
  const { t } = useTranslation("formlar");
  const [tree,        setTree]        = useState(null);
  const [loading,     setLoading]     = useState(true);
  const [error,       setError]       = useState(null);
  const [nodeModal,   setNodeModal]   = useState({ open: false, mode: null, node: null });
  const [deleteModal, setDeleteModal] = useState({ open: false, node: null });

  const isLocked = (template.submissionCount || 0) > 0;

  // Katman bilgisi — çok katmanlı destek
  const sortedLayers   = [...(template.layers || [])].sort((a, b) => Number(a.order) - Number(b.order));
  const categoryLayer  = template.layers?.find((l) => l.type === "CATEGORY") || { order: 0, name: t("freeFormTemplate.bolum"),  type: "CATEGORY" };
  const questionLayer  = template.layers?.find((l) => l.type === "QUESTION") || { order: 1, name: t("freeFormTemplate.soru"),   type: "QUESTION" };

  const loadTree = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getTemplateTree(template._id || template.id);
      // Backend "rootNodes" key kullanıyor → flatFreeFormNodes ile düzleştir (parentId inject edilir)
      if (data) {
        const flat = flatFreeFormNodes(data);
        setTree({ ...data, nodes: flat });
      } else {
        setTree(data);
      }
    } catch (err) {
      setError(err?.response?.data?.message || i18n.t("formlar:freeFormTemplate.sablonYuklenemedi"));
    } finally {
      setLoading(false);
    }
  }, [template]);

  useEffect(() => {
    async function run() { await loadTree(); }
    run();
  }, [loadTree]);

  // Kategoriler — çok katmanlı yardımcılar
  const catLayerOrder  = Number(categoryLayer.order ?? 0);
  const qLayerOrder    = Number(questionLayer.order  ?? 1);
  const rootCategories = tree?.nodes?.filter((n) => Number(n.layerOrder) === catLayerOrder) || [];
  // NodeModal'a geçilecek leaf (QUESTION'ın direkt üstü) kategoriler
  const leafCategories = (tree?.nodes || []).filter(n => {
    if (getLayerInfoFn(n.layerOrder)?.type !== 'CATEGORY') return false;
    const next = getNextLayerFn(n.layerOrder);
    return next?.type === 'QUESTION';
  });
  const categories = leafCategories.length > 0 ? leafCategories : rootCategories; // geriye dönük uyumluluk

  function getLayerInfoFn(layerOrder) {
    return sortedLayers.find(l => Number(l.order) === Number(layerOrder)) || null;
  }
  function getNextLayerFn(layerOrder) {
    const idx = sortedLayers.findIndex(l => Number(l.order) === Number(layerOrder));
    return idx >= 0 && idx < sortedLayers.length - 1 ? sortedLayers[idx + 1] : null;
  }
  function getChildNodesFn(parentId) {
    const pidStr = String(parentId);
    return (tree?.nodes || []).filter(n => String(n.parentId) === pidStr);
  }

  // Soruları kategoriye göre grupla (geriye dönük uyumluluk)
  function getQuestions(categoryId) {
    const catId = String(categoryId?._id ?? categoryId?.id ?? categoryId ?? "");
    return (tree?.nodes || []).filter(
      (n) => Number(n.layerOrder) === qLayerOrder && String(n.parentId) === catId
    );
  }

  // Recursive node renderer
  function renderNodeChildren(parentId, depth) {
    const children = getChildNodesFn(parentId);
    if (children.length === 0) return null;
    const firstChild   = children[0];
    const layerInfo    = getLayerInfoFn(Number(firstChild.layerOrder));

    if (layerInfo?.type === 'QUESTION') {
      return children.map((q, qi) => (
        <div key={q._id || q.id}
          className={`d-flex align-items-start gap-2 p-3 ${qi < children.length - 1 ? "border-bottom" : ""}`}>
          <span className="text-muted small mt-1" style={{ minWidth: 22 }}>{qi + 1}.</span>
          <div className="flex-grow-1">
            <span className="small fw-semibold">{q.name}</span>
            <div className="d-flex gap-2 mt-1 flex-wrap">
              <Badge color="light" className="border text-muted" style={{ fontSize: "0.65rem" }}>
                <AnswerTypeIcon type={q.answerType} />
                {ANSWER_TYPES.find((a) => a.value === q.answerType)?.label || q.answerType}
              </Badge>
              {q.required && <Badge color="danger" pill style={{ fontSize: "0.65rem" }}>{t("freeFormTemplate.zorunlu")}</Badge>}
              {q.choices?.length > 0 && (
                <span className="text-muted" style={{ fontSize: "0.65rem" }}>{q.choices.join(" · ")}</span>
              )}
            </div>
          </div>
          {!isLocked && (
            <div className="d-flex gap-1">
              <Button size="sm" color="soft-secondary" className="py-1 px-2"
                onClick={() => setNodeModal({ open: true, mode: "edit", node: q })}>
                <Edit2 size={12} />
              </Button>
              <Button size="sm" color="soft-danger" className="py-1 px-2"
                onClick={() => setDeleteModal({ open: true, node: q })}>
                <Trash2 size={12} />
              </Button>
            </div>
          )}
        </div>
      ));
    }

    // Alt CATEGORY bölümleri
    return children.map(subCat => {
      const subCatId   = subCat._id || subCat.id;
      const nextLayer  = getNextLayerFn(Number(subCat.layerOrder));
      const subChildren = getChildNodesFn(subCatId);
      return (
        <div key={subCatId} className="border-bottom">
          <div className="d-flex align-items-center gap-2 p-2 border-bottom" style={{ background: '#f8f9fa' }}>
            <FolderOpen size={14} className="text-secondary flex-shrink-0" />
            <span className="fw-semibold small flex-grow-1">{subCat.name}</span>
            <Badge color="secondary" style={{ fontSize: "0.65rem" }}>{subChildren.length}{" "}{t("freeFormTemplate.oge")}</Badge>
            {!isLocked && nextLayer && (
              <>
                <Button size="sm" color="soft-primary" className="py-1 px-2"
                  title={`${nextLayer.name || t("common:ekle")}`}
                  onClick={() => setNodeModal({
                    open: true,
                    mode: nextLayer.type === 'QUESTION' ? "add-question" : "add-category",
                    node: { parentId: subCatId },
                    targetLayerOrder: nextLayer.order,
                  })}>
                  <Plus size={12} />
                </Button>
                <Button size="sm" color="soft-secondary" className="py-1 px-2"
                  onClick={() => setNodeModal({ open: true, mode: "edit", node: subCat })}>
                  <Edit2 size={12} />
                </Button>
                <Button size="sm" color="soft-danger" className="py-1 px-2"
                  onClick={() => setDeleteModal({ open: true, node: subCat })}>
                  <Trash2 size={12} />
                </Button>
              </>
            )}
          </div>
          {subChildren.length === 0 && !isLocked && nextLayer && (
            <div className="text-muted text-center small py-2">
              {t("freeFormTemplate.buBolumde")}{" "}{nextLayer.name || t("freeFormTemplate.oge")}{" "}{t("freeFormTemplate.yok")}
              <button className="btn btn-link btn-sm p-0 ms-2 small text-primary"
                onClick={() => setNodeModal({
                  open: true,
                  mode: nextLayer.type === 'QUESTION' ? "add-question" : "add-category",
                  node: { parentId: subCatId },
                  targetLayerOrder: nextLayer.order,
                })}>
                + {nextLayer.name || t("common:ekle")}
              </button>
            </div>
          )}
          {renderNodeChildren(subCatId, depth + 1)}
        </div>
      );
    });
  }

  return (
    <div>
      {/* Başlık */}
      <div className="d-flex align-items-center gap-2 mb-3 flex-wrap">
        <button className="btn btn-link p-0 text-muted small d-flex align-items-center gap-1" onClick={onBack}>
          <ChevronLeft size={15} />{" "}{t("freeFormTemplate.sablonlaraDon")}
        </button>
        <span className="text-muted mx-1">/</span>
        <span className="fw-semibold">{template.name}</span>
        <EntityBadge type={template.targetEntityType} />
        {isLocked && <LockBadge />}
        {(template.submissionCount > 0) && (
          <Button
            color="soft-info"
            size="sm"
            className="ms-auto d-flex align-items-center gap-1"
            onClick={onViewSubmissions}
          >
            <List size={13} />
            {t("freeFormTemplate.doldurulmusFormuGor", { submissionCount: template.submissionCount })}
          </Button>
        )}
      </div>

      {/* Kilit uyarısı */}
      {isLocked && (
        <Alert color="warning" className="d-flex align-items-center gap-2 mb-3">
          <Lock size={16} />
          <div>
            <strong>{t("freeFormTemplate.sablonKilitli")}</strong>{" "}{t("freeFormTemplate.buSablonAitDoldurulmusFormlar")}
          </div>
        </Alert>
      )}

      {/* Yükleniyor */}
      {loading && (
        <div className="text-center py-5">
          <Spinner color="primary" />
          <p className="text-muted small mt-2">{t("freeFormTemplate.sablonYukleniyor")}</p>
        </div>
      )}

      {error && <Alert color="danger">{error}</Alert>}

      {!loading && !error && (
        <>
          {/* Bölüm ekle butonu */}
          {!isLocked && (
            <Button
              color="soft-primary"
              size="sm"
              className="mb-3"
              onClick={() => setNodeModal({ open: true, mode: "add-category", node: null })}
            >
              <Plus size={13} className="me-1" />
              {t("freeFormTemplate.ekle", { name: categoryLayer.name })}
            </Button>
          )}

          {/* Boş durum */}
          {categories.length === 0 && (
            <Card className="border-0 bg-light text-center py-5">
              <CardBody>
                <FolderOpen size={40} className="text-muted mb-2" />
                <p className="text-muted small mb-0">{t("freeFormTemplate.henuzBolumEklenmemis")}</p>
                {!isLocked && (
                  <p className="text-muted small">
                    {t("freeFormTemplate.yukaridakiEkleButonunuKullanin", { name: categoryLayer.name })}
                  </p>
                )}
              </CardBody>
            </Card>
          )}

          {/* Kategoriler */}
          {rootCategories.map((cat) => {
            const catId     = cat._id || cat.id;
            const nextLayer = getNextLayerFn(Number(cat.layerOrder));
            const directChildren = getChildNodesFn(catId);
            return (
              <Card key={catId} className="border-0 shadow-sm mb-3">
                <CardBody className="p-0">
                  {/* Kategori Başlığı */}
                  <div className="d-flex align-items-center gap-2 p-3 bg-light rounded-top border-bottom">
                    <FolderOpen size={16} className="text-primary" />
                    <span className="fw-semibold small flex-grow-1">{cat.name}</span>
                    <Badge color="secondary" className="me-2" style={{ fontSize: "0.65rem" }}>
                      {directChildren.length} {nextLayer?.name || t("freeFormTemplate.oge")}
                    </Badge>
                    {!isLocked && nextLayer && (
                      <>
                        <Button size="sm" color="soft-primary" className="py-1 px-2"
                          title={`${nextLayer.name || t("common:ekle")}`}
                          onClick={() => setNodeModal({
                            open: true,
                            mode: nextLayer.type === 'QUESTION' ? "add-question" : "add-category",
                            node: { parentId: catId },
                            targetLayerOrder: nextLayer.order,
                          })}>
                          <Plus size={12} />
                        </Button>
                        <Button size="sm" color="soft-secondary" className="py-1 px-2"
                          title={t("common:duzenle")}
                          onClick={() => setNodeModal({ open: true, mode: "edit", node: cat })}>
                          <Edit2 size={12} />
                        </Button>
                        <Button size="sm" color="soft-danger" className="py-1 px-2"
                          title={t("common:sil")}
                          onClick={() => setDeleteModal({ open: true, node: cat })}>
                          <Trash2 size={12} />
                        </Button>
                      </>
                    )}
                  </div>

                  {/* Boş durum */}
                  {directChildren.length === 0 && !isLocked && nextLayer && (
                    <div className="text-muted text-center small py-3">
                      {t("freeFormTemplate.buBolumde")}{" "}{nextLayer.name || t("freeFormTemplate.oge")}{" "}{t("freeFormTemplate.yok")}
                      <button className="btn btn-link btn-sm p-0 ms-2 small text-primary"
                        onClick={() => setNodeModal({
                          open: true,
                          mode: nextLayer.type === 'QUESTION' ? "add-question" : "add-category",
                          node: { parentId: catId },
                          targetLayerOrder: nextLayer.order,
                        })}>
                        + {nextLayer.name || t("common:ekle")}
                      </button>
                    </div>
                  )}

                  {/* Recursive içerik */}
                  {renderNodeChildren(catId, 0)}
                </CardBody>
              </Card>
            );
          })}
        </>
      )}

      {/* Modaller */}
      <NodeModal
        open={nodeModal.open}
        mode={nodeModal.mode}
        node={nodeModal.node}
        categoryLayer={categoryLayer}
        questionLayer={questionLayer}
        templateId={template._id || template.id}
        categories={categories}
        targetLayerOrder={nodeModal.targetLayerOrder}
        onClose={() => setNodeModal({ open: false, mode: null, node: null })}
        onSaved={loadTree}
      />
      <DeleteNodeModal
        open={deleteModal.open}
        node={deleteModal.node}
        onClose={() => setDeleteModal({ open: false, node: null })}
        onDeleted={loadTree}
      />
    </div>
  );
}

// ─── Ana Bileşen ──────────────────────────────────────────────────────────────

// embedded: boolean true → sayfa kabuğu kapanır (sekme olarak render edilir)
export default function FreeFormTemplatePage({ embedded = false }) {
  const { t } = useTranslation("formlar");
  useEffect(() => { document.title = i18n.t("formlar:freeFormTemplate.esnekFormSablonlariFds"); }, []);
  const navigate = useNavigate();

  const [templates,     setTemplates]     = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [filterType,    setFilterType]    = useState("");
  const [page,          setPage]          = useState(1);
  const [totalPages,    setTotalPages]    = useState(1);
  const [error,         setError]         = useState(null);

  const [selectedTemplate, setSelectedTemplate] = useState(null); // node editörü için
  const [createModal,  setCreateModal]  = useState(false);
  const [editModal,    setEditModal]    = useState({ open: false, template: null });
  const [deleteModal,  setDeleteModal]  = useState({ open: false, template: null });

  const LIMIT = 12;

  const loadTemplates = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // ⚠️ Backend 0-tabanlı sayfalama kullanır → page-1 gönder
      const params = { page: page - 1, limit: LIMIT };
      if (filterType) params.targetEntityType = filterType;
      const data = await listTemplates(params);
      // Backend paginated response: { content: [], totalPages: n } veya düz array
      if (Array.isArray(data)) {
        setTemplates(data);
        setTotalPages(1);
      } else {
        setTemplates(data.records || data.content || data.templates || []);
        setTotalPages(data.totalPages || Math.ceil((data.total || 0) / LIMIT) || 1);
      }
    } catch (err) {
      setError(err?.response?.data?.message || i18n.t("formlar:freeFormTemplate.sablonlarYuklenemedi"));
    } finally {
      setLoading(false);
    }
  }, [page, filterType]);

  useEffect(() => {
    async function run() { if (!selectedTemplate) await loadTemplates(); }
    run();
  }, [loadTemplates, selectedTemplate]);

  // Node editörüne geçince listeyi yenile sıfırlama
  function openNodeEditor(template) {
    setSelectedTemplate(template);
  }

  function backToList() {
    setSelectedTemplate(null);
    // Listeyi yenile
    loadTemplates();
  }

  // ── Node editörü görünümü ──
  if (selectedTemplate) {
    return (
      <PageFrame embedded={embedded}>
        <PageBody embedded={embedded} title={t("freeFormTemplate.esnekFormSablonlari")} pageTitle={t("freeFormTemplate.yonetim")}>
          <NodeEditor
            template={selectedTemplate}
            onBack={backToList}
            onViewSubmissions={() => navigate(`/Management/FreeForm/submissions?templateId=${selectedTemplate._id || selectedTemplate.id}`)}
          />
        </PageBody>
      </PageFrame>
    );
  }

  // ── Şablon listesi görünümü ──
  return (
    <PageFrame embedded={embedded}>
      <PageBody embedded={embedded} title={t("freeFormTemplate.esnekFormSablonlari")} pageTitle={t("freeFormTemplate.yonetim")}>

        {/* Araç çubuğu */}
        <div className="d-flex align-items-center gap-2 mb-3 flex-wrap">
          <h5 className="fw-bold mb-0 me-auto">{t("freeFormTemplate.esnekFormSablonlari")}</h5>

          {/* Filtre */}
          {ENTITY_TYPES.map((et) => (
            <Button
              key={et.value}
              size="sm"
              color={filterType === et.value ? (ENTITY_COLORS[et.value] || "primary") : "light"}
              outline={filterType !== et.value}
              onClick={() => { setFilterType(et.value); setPage(1); }}
            >
              {et.label}
            </Button>
          ))}

          <Button
            color="primary"
            size="sm"
            className="ms-2"
            onClick={() => setCreateModal(true)}
          >
            <Plus size={14} className="me-1" />{" "}{t("freeFormTemplate.yeniSablon")}
          </Button>
        </div>

        {error && <Alert color="danger">{error}</Alert>}

        {loading ? (
          <div className="text-center py-5">
            <Spinner color="primary" />
            <p className="text-muted small mt-2">{t("common:yukleniyor")}</p>
          </div>
        ) : templates.length === 0 ? (
          <Card className="border-0 shadow-sm text-center py-5">
            <CardBody>
              <FolderOpen size={48} className="text-muted mb-3" />
              <h6 className="text-muted">{t("freeFormTemplate.henuzSablonOlusturulmamis")}</h6>
              <p className="text-muted small">
                {t("freeFormTemplate.yeniSablonButonunaTiklayarakIlk")}
              </p>
              <Button color="primary" size="sm" onClick={() => setCreateModal(true)}>
                <Plus size={14} className="me-1" />{" "}{t("freeFormTemplate.yeniSablonOlustur")}
              </Button>
            </CardBody>
          </Card>
        ) : (
          <>
            <Row className="g-3">
              {templates.map((tmpl) => {
                const isLocked = (tmpl.submissionCount || 0) > 0;
                return (
                  <Col key={tmpl._id || tmpl.id} xs={12} sm={6} lg={4} xl={3}>
                    <Card className="border-0 shadow-sm h-100">
                      <CardBody className="d-flex flex-column gap-2">
                        {/* Başlık + badges */}
                        <div className="d-flex align-items-start gap-2">
                          <div className="flex-grow-1">
                            <div className="fw-semibold small">{tmpl.name}</div>
                            {tmpl.description && (
                              <div className="text-muted mt-1" style={{ fontSize: "0.72rem", lineHeight: 1.4 }}>
                                {tmpl.description}
                              </div>
                            )}
                          </div>
                          {isLocked && <LockBadge />}
                        </div>

                        {/* Meta */}
                        <div className="d-flex gap-2 flex-wrap">
                          <EntityBadge type={tmpl.targetEntityType} />
                          {(tmpl.submissionCount != null) && (
                            <Badge color="light" className="border text-muted" style={{ fontSize: "0.65rem" }}>
                              {t("freeFormTemplate.doldurulmus", { submissionCount: tmpl.submissionCount })}
                            </Badge>
                          )}
                          {(tmpl.nodeCount != null) && (
                            <Badge color="light" className="border text-muted" style={{ fontSize: "0.65rem" }}>
                              {t("freeFormTemplate.node", { nodeCount: tmpl.nodeCount })}
                            </Badge>
                          )}
                        </div>

                        {/* Katmanlar */}
                        {tmpl.layers?.length > 0 && (
                          <div className="d-flex gap-1 flex-wrap">
                            {tmpl.layers.map((l, li) => (
                              <Badge key={li} color={l.type === "CATEGORY" ? "primary" : "success"}
                                style={{ fontSize: "0.6rem", fontWeight: "normal" }}>
                                {l.type === "CATEGORY" ? <FolderOpen size={9} className="me-1" /> : <HelpCircle size={9} className="me-1" />}
                                {l.name}
                              </Badge>
                            ))}
                          </div>
                        )}

                        {/* Aksiyon butonları */}
                        <div className="d-flex gap-2 mt-auto flex-wrap">
                          <Button
                            color="soft-primary"
                            size="sm"
                            className="flex-grow-1"
                            onClick={() => openNodeEditor(tmpl)}
                          >
                            <Edit2 size={12} className="me-1" />
                            {isLocked ? t("common:goruntule") : t("common:duzenle")}
                          </Button>
                          {(tmpl.submissionCount > 0) && (
                            <Button
                              color="soft-info"
                              size="sm"
                              title={t("freeFormTemplate.doldurulmusFormlariGoruntule")}
                              onClick={() => navigate(`/Management/FreeForm/submissions?templateId=${tmpl._id || tmpl.id}`)}
                            >
                              <List size={12} className="me-1" />
                              {t("freeFormTemplate.kayit", { submissionCount: tmpl.submissionCount })}
                            </Button>
                          )}
                          {!isLocked && (
                            <Button
                              color="soft-secondary"
                              size="sm"
                              title={t("freeFormTemplate.sablonBilgileriniGuncelle")}
                              onClick={() => setEditModal({ open: true, template: tmpl })}
                            >
                              <Save size={12} />
                            </Button>
                          )}
                          <Button
                            color="soft-danger"
                            size="sm"
                            title={t("freeFormTemplate.sablonuSil2")}
                            onClick={() => setDeleteModal({ open: true, template: tmpl })}
                          >
                            <Trash2 size={12} />
                          </Button>
                        </div>
                      </CardBody>
                    </Card>
                  </Col>
                );
              })}
            </Row>

            {/* Sayfalama */}
            {totalPages > 1 && (
              <div className="d-flex justify-content-center align-items-center gap-2 mt-4">
                <Button size="sm" color="light" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
                  <ChevronLeft size={14} />
                </Button>
                <span className="small text-muted">{page} / {totalPages}</span>
                <Button size="sm" color="light" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>
                  <ChevronRight size={14} />
                </Button>
              </div>
            )}
          </>
        )}
      </PageBody>

      {/* Modaller */}
      <TemplateFormModal
        open={createModal}
        template={null}
        onClose={() => setCreateModal(false)}
        onSaved={loadTemplates}
      />
      <TemplateFormModal
        open={editModal.open}
        template={editModal.template}
        onClose={() => setEditModal({ open: false, template: null })}
        onSaved={loadTemplates}
      />
      <DeleteTemplateModal
        open={deleteModal.open}
        template={deleteModal.template}
        onClose={() => setDeleteModal({ open: false, template: null })}
        onDeleted={loadTemplates}
      />
    </PageFrame>
  );
}

